import React, { useEffect, useState } from "react";
import { api } from "./api";

// "threats" = suspected + confirmed items only. The two "rules:" columns are rule flags (before the model and any
// human), shown separately so they are never read as findings. Flags are dated by when the item's current content
// arrived, so a rules re-score does not move them onto the re-score day.
const COLS = [
  ["new_items", "items"], ["new_versions", "new versions"], ["threats", "threats"],
  ["threat_signals", "rules: threat flags"], ["warning_signals", "rules: warnings"],
  ["requests", "requests"], ["llm_calls", "LLM calls"], ["llm_cost_aud", "LLM A$"],
];
const HELP = {
  threats: "Suspected + confirmed items (rules and the model agree, or a human / automated confirmation)",
  threat_signals: "Items whose rules raised a threat-level signal; most are not findings until the model agrees",
  warning_signals: "Items whose worst rules signal is a warning",
};

// Dashboard card: per source and per day (UTC) items, new versions, threats, rule flags, requests, LLM spend.
export default function SourcesOverview({ styles }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [days, setDays] = useState(3);
  useEffect(() => {
    setData(null);
    api.sourcesOverview(days).then((r) => setData(r.data)).catch((ex) => setErr(ex.message));
  }, [days]);
  if (err) return <div style={{ ...styles.card, color: "var(--hs-danger)" }}>Could not load sources overview: {err}</div>;
  if (!data) return <div style={styles.card}>Loading sources overview…</div>;
  return (
    <div style={styles.card} data-testid="sources-overview">
      <div style={styles.row}>
        <h3 style={{ margin: 0 }}>Sources overview</h3>
        <span style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>per source per day (UTC) · global LLM cap A${data.llm_daily_cap_aud}/day</span>
        <select data-testid="overview-days" style={{ ...styles.select, marginLeft: "auto" }} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[1, 3, 7, 14].map((d) => <option key={d} value={d}>{d} day{d > 1 ? "s" : ""}</option>)}
        </select>
      </div>
      <table style={styles.tbl}>
        <thead>
          <tr>
            <th style={styles.th}>Source</th><th style={styles.th}>Day</th>
            {COLS.map(([k, label]) => <th key={k} style={styles.th} title={HELP[k]}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.sources.map((s) => data.days.map((d, i) => {
            const c = s.days[d] || {};
            return (
              <tr key={`${s.id}-${d}`} style={!s.enabled ? { color: "var(--hs-text-muted)" } : undefined}>
                <td style={styles.td}>{i === 0 && <><strong>{s.name}</strong>
                  <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{s.kind} · {s.enabled ? "on" : "off"} · {s.items_total} items
                    {s.llm_daily_share != null && <> · LLM share {Math.round(s.llm_daily_share * 100)}%</>}</div></>}</td>
                <td style={styles.td}>{d}</td>
                {COLS.map(([k]) => (
                  <td key={k} data-testid={`ov-${s.id}-${d}-${k}`}
                      title={k === "threats" ? `${c.suspected ?? 0} suspected · ${c.confirmed ?? 0} confirmed` : undefined}
                      style={{ ...styles.td, color: k === "threats" && c[k] > 0 ? "var(--hs-danger)" : (k === "threat_signals" || k === "warning_signals") && c[k] > 0 ? "var(--hs-text-muted)" : undefined }}>
                    {k === "llm_cost_aud" ? Number(c[k] || 0).toFixed(3) : (c[k] ?? 0)}
                  </td>
                ))}
              </tr>
            );
          }))}
          {data.sources.length === 0 && <tr><td style={styles.td} colSpan={2 + COLS.length}>No sources yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
